import { ReactNode } from 'react'
import { TableHead } from '@clickguard/design-system'
import ArrowUp from '@untitled-ui/icons-react/build/esm/ArrowUp'
import ChevronSelectorVertical from '@untitled-ui/icons-react/build/esm/ChevronSelectorVertical'

export type SortState = { key: string; dir: 'asc' | 'desc' } | null

/** Click cycle: another column → asc → desc → unsorted. */
export const nextSortState = (current: SortState, key: string): SortState => {
	if (current?.key !== key) return { key, dir: 'asc' }
	if (current.dir === 'asc') return { key, dir: 'desc' }
	return null
}

/**
 * The canonical sortable table header cell, copied from the Custom Rules table.
 * The chevron is ALWAYS visible, so every sortable column advertises itself
 * before the pointer arrives. The DS `TableHead sortable` prop does not do
 * this: it paints its own chevron at `cg-opacity-0 group-hover:cg-opacity-40`
 * and never sets the `group` class, so that chevron never appears at all.
 */
export const SortHeadCell = ({
	label,
	sortKey,
	sort,
	onSort,
	align,
	className,
	children
}: {
	label: string
	sortKey: string
	sort: SortState
	onSort: (key: string) => void
	align?: 'right'
	/** Extra classes on the TableHead. */
	className?: string
	/**
	 * Optional extra content, such as a help tooltip. It sits between the label
	 * and the sort chevron, so it reads as part of the column name.
	 *
	 * 🪤 It renders INSIDE the sort button, because that is the only way to put
	 * it between the two. The wrapper below stops the click, so a press on the
	 * accessory does not sort the column. Keep the accessory NON-INTERACTIVE for
	 * the same reason: a button or a focusable element inside a button is invalid
	 * HTML and the keyboard cannot reach it.
	 */
	children?: ReactNode
}) => {
	const dir = sort?.key === sortKey ? sort.dir : null
	return (
		<TableHead
			className={[align === 'right' ? '!text-right' : '', className ?? '']
				.join(' ')
				.trim()}
		>
			{/* `cg-shadow-focus-sm` is the DS focus ring; without it a bare button
			    falls back to Chrome's default blue outline. It is a box-shadow
			    spread, so it paints OUTSIDE the button and needs no padding.
			    Do NOT add a `-mx-0.5 px-0.5` pair here for breathing room. On the
			    rules table that pair truncated every label: the column is sized
			    from the header's max-content width, the negative margin cancelled
			    the padding in that measurement, and the padding then took 4px back
			    out of the label from the inside. */}
			<button
				type="button"
				onClick={() => onSort(sortKey)}
				className={`group inline-flex max-w-full items-center gap-1 rounded text-left align-middle focus-visible:cg-shadow-focus-sm focus-visible:outline-none ${
					align === 'right' ? 'flex-row-reverse' : ''
				}`}
				aria-label={`Sort by ${label}${
					dir ? (dir === 'asc' ? ', ascending' : ', descending') : ''
				}`}
			>
				<span className="truncate">{label}</span>
				{children ? (
					<span
						className="inline-flex shrink-0 items-center"
						onClick={(event) => {
							// The accessory belongs to the label, not to the sort. Without
							// this the click bubbles to the button and reorders the table.
							event.stopPropagation()
							event.preventDefault()
						}}
					>
						{children}
					</span>
				) : null}
				<span className="shrink-0 text-[rgb(var(--cg-text-tertiary))] transition-colors group-hover:text-[rgb(var(--cg-text-secondary))]">
					{/* One arrow that TURNS, not two arrows that swap. React keeps
					    the same element across asc → desc, so the 180° turn plays.
					    A swap gives you nothing to look at and loses the link
					    between the direction you picked and the rows that move. */}
					{dir ? (
						<ArrowUp
							className={`h-3.5 w-3.5 transition-transform duration-200 ease-[cubic-bezier(0.16,1,0.3,1)] motion-reduce:transition-none ${
								dir === 'desc' ? 'rotate-180' : ''
							}`}
						/>
					) : (
						<ChevronSelectorVertical className="h-3.5 w-3.5" />
					)}
				</span>
			</button>
		</TableHead>
	)
}
